import React, { useEffect, useState } from "react";
import { Button, Form, Table, Alert, Card } from "react-bootstrap";
import axios from "axios";
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";

import { loggedInUserId } from "../../utils/loggedInUserDetails";
import PaymentTermsSelect from "../../components/common/PaymentTermsSelect";

export default function AddPackagingMaterialPO() {
  const navigate = useNavigate();
  const location = useLocation();
  // Start at step 1 by default, but will be overridden in useEffect if pre-selected
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState({
    supplier: "",
    orderDate: new Date().toISOString().split("T")[0],
    expectedDeliveryDate: "",
    paymentTerms: "Credit 30",
    shippingCharges: 0,
    remarks: "",
    deliveryFrom: "",
    deliveryTo: "",
    createdBy: loggedInUserId(),
  });

  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({
    packingMaterial: "",
    quantity: 0,
    unit: "pcs",
    unitPrice: 0,
    taxRate: 0,
    totalAmount: 0
  });

  const [suppliers, setSuppliers] = useState([]);
  const [packingMaterials, setPackingMaterials] = useState([]);

  // Permission checks
  const canCreatePO = useHasPermission(PERMISSIONS.PM_PO_CREATE);
  const canViewVendors = useHasPermission(PERMISSIONS.VIEW_VENDORS);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [];

        if (canViewVendors) {
          promises.push(axios.get(`${config.API_BASE_URL}/vendors?approved=true&status=Active`));
        }

        promises.push(axios.get(`${config.API_BASE_URL}/packing-materials`));

        const responses = await Promise.all(promises);

        let responseIndex = 0;
        if (canViewVendors) {
          setSuppliers(responses[responseIndex].data);
          responseIndex++;
        }

        const pmData = responses[responseIndex].data;
        setPackingMaterials(pmData);

        // Handle pre-selected material from stock cards
        const locationState = location.state;
        if (locationState?.preSelectedMaterial) {
          // Find the selected packing material
          const selectedPM = pmData.find(pm => pm._id === locationState.preSelectedMaterial._id);
          if (selectedPM) {
            // Jump to step 2 and pre-fill the item
            setStep(2);
            setNewItem({
              packingMaterial: selectedPM._id,
              quantity: Math.max((locationState.reorderLevel || 0) * 2, 1), // Suggest 2x reorder level
              unit: selectedPM.unit || "pcs",
              unitPrice: 0,
              taxRate: 0,
              totalAmount: 0
            });
          }
        }

        // Auto-fill delivery from with selected supplier's address
        const firstSupplier = responses[0]?.data?.find(s => s._id === orderData.supplier);
        if (firstSupplier) {
          setOrderData(prev => ({
            ...prev,
            deliveryFrom: `${firstSupplier.business_name}\n${firstSupplier.address}\n${firstSupplier.town}, ${firstSupplier.city}, ${firstSupplier.state} - ${firstSupplier.pin_code}`
          }));
        }

        // Auto-fill company address for delivery to
        const companySettingsRes = await axios.get(`${config.API_BASE_URL}/company-settings`);
        if (companySettingsRes.data?.address) {
          const addr = companySettingsRes.data.address;
          setOrderData(prev => ({
            ...prev,
            deliveryTo: `${addr.street}\n${addr.city} ${addr.state}\n${addr.pincode}`
          }));
        }

      } catch (err) {
        console.error("Error fetching data:", err);
        toast.error("Failed to load necessary data");
      }
    };

    fetchData();
  }, [canViewVendors, location.state, orderData.supplier]);

  const handleOrderChange = (e) => {
    setOrderData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    let updatedItem = { ...newItem, [name]: value };

    // Auto-calc total amount when qty, price, or tax changes
    if (name === "quantity" || name === "unitPrice" || name === "taxRate") {
      const qty = name === "quantity" ? parseFloat(value) : parseFloat(updatedItem.quantity);
      const price = name === "unitPrice" ? parseFloat(value) : parseFloat(updatedItem.unitPrice);
      const taxRate = name === "taxRate" ? parseFloat(value) : parseFloat(updatedItem.taxRate);

      const baseAmount = qty * price;
      const taxAmount = (baseAmount * taxRate) / 100;
      updatedItem.totalAmount = baseAmount + taxAmount;
    }

    setNewItem(updatedItem);
  };

  const addItem = () => {
    if (!newItem.packingMaterial || newItem.quantity <= 0 || newItem.unitPrice <= 0) {
      toast.error("Please fill packing material, quantity, and unit price");
      return;
    }
    setItems(prev => [...prev, newItem]);
    setNewItem({
      packingMaterial: "",
      quantity: 0,
      unit: "pcs",
      unitPrice: 0,
      taxRate: 0,
      totalAmount: 0,
      status: "Pending"
    });
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep1 = () => {
    if (!orderData.supplier) {
      toast.error("Please select a supplier");
      return false;
    }
    if (!orderData.expectedDeliveryDate) {
      toast.error("Please select expected delivery date");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (items.length === 0) {
      toast.error("Please add at least one item");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!canCreatePO) {
      toast.error("You don't have permission to create packaging material purchase orders");
      return;
    }

    if (!validateStep2()) return;

    setLoading(true);
    try {
      // Calculate totals
      let subtotal = 0;
      let totalTaxAmount = 0;

      const processedItems = items.map(item => {
        const baseAmount = item.quantity * item.unitPrice;
        const taxAmount = (baseAmount * item.taxRate) / 100;
        const totalAmount = baseAmount + taxAmount;

        subtotal += baseAmount;
        totalTaxAmount += taxAmount;

        return {
          packingMaterial: item.packingMaterial,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate,
          taxAmount: taxAmount,
          totalAmount: totalAmount,
          status: "Pending"
        };
      });

      const totalAmount = subtotal + totalTaxAmount + parseFloat(orderData.shippingCharges || 0);

      const orderPayload = {
        ...orderData,
        items: processedItems,
        subtotal: subtotal,
        taxAmount: totalTaxAmount,
        totalAmount: totalAmount
      };

      await axios.post(`${config.API_BASE_URL}/pm-purchase-orders`, orderPayload);

      toast.success("Packaging Material Purchase Order created successfully");
      navigate("/packaging-material-purchase-orders", {
        state: { toast: "Packaging Material Purchase Order created successfully" }
      });
    } catch (err) {
      console.error("Error saving packaging material PO", err);
      toast.error("Error saving purchase order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // If user can't create PO, show permission denied
  if (!canCreatePO) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/packaging-material-purchase-orders">Packaging Material POs</Link></li>
            <li className="breadcrumb-item active">Add New</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to create packaging material purchase orders. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/packaging-material-purchase-orders">Packaging Material POs</Link></li>
          <li className="breadcrumb-item active">Add New</li>
        </ol>
      </nav>

      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">
                {location.state?.preSelectedMaterial ? 'Quick Purchase Order' : 'Add Packaging Material PO'}
              </h2>
              <small className="text-muted">
                {location.state?.preSelectedMaterial ? 'Direct purchasing for selected material' : `Step ${step} of 2`}
              </small>
              {location.state?.preSelectedMaterial && (
                <small className="text-success d-block">
                  <i className="fas fa-lightbulb me-1"></i>
                  Pre-selected: {packingMaterials.find(pm => pm._id === location.state.preSelectedMaterial._id)?.name}
                </small>
              )}
            </div>
            <div className="progress" style={{ width: '200px', height: '6px' }}>
              <div
                className="progress-bar"
                style={{ width: `${(step / 2) * 100}%` }}
              ></div>
            </div>
          </div>

          {step === 1 && (
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-info-circle me-2"></i>Order Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Form>
                  <div className="row">
                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>
                        Supplier <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="supplier"
                        value={orderData.supplier}
                        onChange={handleOrderChange}
                        disabled={!canViewVendors}
                      >
                        <option value="">Select Supplier...</option>
                        {suppliers.map(v => (
                          <option key={v._id} value={v._id}>{v.business_name}</option>
                        ))}
                      </Form.Select>
                      {!canViewVendors && (
                        <Form.Text className="text-warning">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          Limited supplier access - contact admin
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>
                        Expected Delivery Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="expectedDeliveryDate"
                        value={orderData.expectedDeliveryDate}
                        onChange={handleOrderChange}
                        min={new Date().toISOString().split("T")[0]}
                      />
                    </Form.Group>
                  </div>

                  <div className="row">
                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>Payment Terms</Form.Label>
                      <PaymentTermsSelect
                        value={orderData.paymentTerms}
                        onChange={(value) => setOrderData(prev => ({ ...prev, paymentTerms: value }))}
                      />
                    </Form.Group>

                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>Shipping Charges (₹)</Form.Label>
                      <Form.Control
                        type="number"
                        name="shippingCharges"
                        value={orderData.shippingCharges}
                        onChange={handleOrderChange}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </div>

                  <div className="row">
                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>Delivery To</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="deliveryTo"
                        value={orderData.deliveryTo}
                        onChange={handleOrderChange}
                        placeholder="Enter delivery address"
                      />
                    </Form.Group>

                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>Delivery From</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="deliveryFrom"
                        value={orderData.deliveryFrom}
                        onChange={handleOrderChange}
                        placeholder="Enter pickup address"
                      />
                    </Form.Group>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="remarks"
                      value={orderData.remarks}
                      onChange={handleOrderChange}
                      placeholder="Additional notes or instructions"
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-between">
                    <Link to="/packaging-material-purchase-orders" className="btn btn-secondary">
                      <i className="fas fa-arrow-left me-2"></i>Cancel
                    </Link>
                    <Button
                      onClick={() => {
                        if (validateStep1()) {
                          setStep(2);
                        }
                      }}
                      className="btn btn-primary"
                    >
                      Next: Add Items <i className="fas fa-arrow-right ms-2"></i>
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-box me-2"></i>Packaging Materials
                </h5>
              </Card.Header>
              <Card.Body>
                <Alert variant="info">
                  <i className="fas fa-info-circle me-2"></i>
                  Select packaging materials, quantities, and pricing to create your purchase order.
                </Alert>

                <Form className="mb-4 border rounded p-3 bg-light">
                  <h6 className="mb-3">Add Item</h6>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-2 form-floating">
                      <Form.Select
                        id="packingMaterialSelect"
                        name="packingMaterial"
                        value={newItem.packingMaterial}
                        onChange={handleItemChange}
                      >
                        <option value="">Select Material</option>
                        {packingMaterials.map(pm => (
                          <option key={pm._id} value={pm._id}>
                            {pm.name} {pm.alias ? `(${pm.alias})` : ''} {pm.size ? `- ${pm.size}` : ''}
                          </option>
                        ))}
                      </Form.Select>
                      <label htmlFor="packingMaterialSelect">Packing Material *</label>
                    </div>

                    <div className="col-md-2 form-floating">
                      <Form.Control
                        id="quantityInput"
                        type="number"
                        name="quantity"
                        placeholder="Quantity"
                        value={newItem.quantity}
                        onChange={handleItemChange}
                        min="0"
                        step="0.01"
                      />
                      <label htmlFor="quantityInput">Quantity *</label>
                    </div>

                    <div className="col-md-2 form-floating">
                      <Form.Select
                        id="unitSelect"
                        name="unit"
                        value={newItem.unit}
                        onChange={handleItemChange}
                      >
                        <option value="pcs">Pieces</option>
                        <option value="kg">Kilograms</option>
                        <option value="g">Grams</option>
                        <option value="m">Meters</option>
                        <option value="cm">Centimeters</option>
                        <option value="l">Liters</option>
                        <option value="ml">Milliliters</option>
                        <option value="box">Boxes</option>
                        <option value="pack">Packs</option>
                      </Form.Select>
                      <label htmlFor="unitSelect">Unit</label>
                    </div>

                    <div className="col-md-2 form-floating">
                      <Form.Control
                        id="unitPriceInput"
                        type="number"
                        name="unitPrice"
                        placeholder="Unit Price"
                        value={newItem.unitPrice}
                        onChange={handleItemChange}
                        min="0"
                        step="0.01"
                      />
                      <label htmlFor="unitPriceInput">Unit Price (₹) *</label>
                    </div>

                    <div className="col-md-2 form-floating">
                      <Form.Control
                        id="taxRateInput"
                        type="number"
                        name="taxRate"
                        placeholder="Tax %"
                        value={newItem.taxRate}
                        onChange={handleItemChange}
                        min="0"
                        max="100"
                        step="0.01"
                      />
                      <label htmlFor="taxRateInput">Tax Rate (%)</label>
                    </div>

                    <div className="col-md-1 form-floating">
                      <Form.Control
                        type="number"
                        value={newItem.totalAmount?.toFixed(2) || "0.00"}
                        readOnly
                        className="bg-light"
                      />
                      <label>Total (₹)</label>
                    </div>

                    <div className="col-md-1">
                      <Button
                        variant="success"
                        onClick={addItem}
                        className="w-100"
                      >
                        <i className="fas fa-plus"></i>
                      </Button>
                    </div>
                  </div>
                </Form>

                {items.length > 0 && (
                  <>
                    <div className="table-responsive">
                      <Table bordered hover className="mb-4">
                        <thead className="table-light">
                          <tr>
                            <th>Packing Material</th>
                            <th>Quantity</th>
                            <th>Unit Price</th>
                            <th>Tax (%)</th>
                            <th>Total</th>
                            <th width="80px">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it, i) => {
                            const packingMaterial = packingMaterials.find(pm => pm._id === it.packingMaterial);
                            return (
                              <tr key={i}>
                                <td>
                                  <div>
                                    <strong>{packingMaterial?.name || 'N/A'}</strong>
                                    {packingMaterial?.alias && <div className="small text-muted">({packingMaterial.alias})</div>}
                                  </div>
                                </td>
                                <td>{it.quantity} {it.unit}</td>
                                <td>₹{parseFloat(it.unitPrice).toFixed(2)}</td>
                                <td>{it.taxRate}%</td>
                                <td>₹{it.totalAmount?.toFixed(2) || "0.00"}</td>
                                <td className="text-center">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeItem(i)}
                                    title="Remove item"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>

                    <div className="row justify-content-end">
                      <div className="col-md-6">
                        <Card>
                          <Card.Body>
                            <div className="row mb-2">
                              <div className="col-12">
                                <div className="d-flex justify-content-between">
                                  <span>Subtotal:</span>
                                  <strong>₹{items.reduce((sum, it) => sum + (it.quantity * it.unitPrice), 0).toFixed(2)}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <span>Tax Amount:</span>
                                  <strong>₹{items.reduce((sum, it) => (it.totalAmount - (it.quantity * it.unitPrice)) || 0, 0).toFixed(2)}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <span>Shipping Charges:</span>
                                  <strong>₹{parseFloat(orderData.shippingCharges || 0).toFixed(2)}</strong>
                                </div>
                              </div>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between align-items-center">
                              <strong>Grand Total:</strong>
                              <strong className="text-success fs-4">
                                ₹{(() => {
                                  const subtotal = items.reduce((sum, it) => sum + (it.quantity * it.unitPrice), 0);
                                  const taxAmount = items.reduce((sum, it) => (it.totalAmount - (it.quantity * it.unitPrice)) || 0, 0);
                                  const shipping = parseFloat(orderData.shippingCharges || 0);
                                  return (subtotal + taxAmount + shipping).toFixed(2);
                                })()}
                              </strong>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    </div>
                  </>
                )}

                {items.length === 0 && (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-box fa-2x mb-2"></i>
                    <p>No items added yet. Add packaging materials to continue.</p>
                  </div>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStep(1)}
                  >
                    <i className="fas fa-arrow-left me-2"></i>Back
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleSave}
                    disabled={items.length === 0 || loading}
                  >
                    {loading ? 'Creating...' : 'Create PM Purchase Order'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
